import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Alert
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Event as EventIcon,
  Announcement as AnnouncementIcon,
  AdUnits as AdsIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../utils/api';
import { ensureHttps } from '../utils/url';

interface Promotion {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  linkUrl?: string;
  type: 'promotion' | 'event' | 'advertisement' | 'announcement';
  displayLocation: 'neighborhoodpulse' | 'transferhub' | 'both';
  startDate?: string;
  endDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const TransferHubPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promotions/transferhub`);
      if (response.ok) {
        const data = await response.json();
        setPromotions(data);
      } else {
        setError('Failed to load promotions');
      }
    } catch (error) {
      setError('Failed to load promotions');
      console.error('Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'promotion':
        return <CampaignIcon />;
      case 'event':
        return <EventIcon />;
      case 'advertisement':
        return <AdsIcon />;
      case 'announcement':
        return <AnnouncementIcon />;
      default:
        return <CampaignIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'promotion':
        return 'primary';
      case 'event':
        return 'success';
      case 'advertisement':
        return 'warning';
      case 'announcement':
        return 'info';
      default:
        return 'default';
    }
  };

  const isPromotionActive = (promotion: Promotion) => {
    if (!promotion.isActive) return false;
    
    const now = new Date();
    const startDate = promotion.startDate ? new Date(promotion.startDate) : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;
    
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    
    return true;
  };

  const activePromotions = promotions.filter(isPromotionActive);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading promotions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (activePromotions.length === 0) {
    return null; // Don't show anything if no active promotions
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CampaignIcon sx={{ mr: 1 }} />
        Featured Promotions
      </Typography>
      
      <Grid container spacing={2}>
        {activePromotions.map((promotion) => (
          <Grid item xs={12} md={6} key={promotion.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {promotion.imageUrl && (
                <Box
                  component="img"
                  src={ensureHttps(promotion.imageUrl)}
                  alt={promotion.title}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover'
                  }}
                />
              )}
              
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip
                    icon={getTypeIcon(promotion.type)}
                    label={promotion.type.charAt(0).toUpperCase() + promotion.type.slice(1)}
                    color={getTypeColor(promotion.type) as any}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                </Box>
                
                <Typography variant="h6" gutterBottom>
                  {promotion.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
                  {promotion.description}
                </Typography>
                
                {promotion.linkUrl && (
                  <Button
                    variant="contained"
                    size="small"
                    href={promotion.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Learn More
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TransferHubPromotions;


